<template>
  <div>
    <Breadcrumbs title="Features" />

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">
                  <i class="fas fa-puzzle-piece me-2"></i>
                  Feature Management
                </h4>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                    <i class="fas fa-plus me-1"></i>
                    Add Feature
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters and Search -->
              <div class="row mb-4">
                <div class="col-md-4">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search features..."
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <select
                    v-model="filterCategory"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Categories</option>
                    <option
                      v-for="category in categories"
                      :key="category._id"
                      :value="category._id"
                    >
                      {{ category.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="filterStatus"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <select
                    v-model="sortBy"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="name">Name</option>
                    <option value="key">Key</option>
                    <option value="category">Category</option>
                    <option value="createdAt">Created Date</option>
                  </select>
                </div>
                <div class="col-md-1">
                  <button
                    class="btn btn-outline-secondary w-100"
                    @click="resetFilters"
                    title="Reset Filters"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Features Table -->
              <div v-else-if="features.length > 0" class="table-responsive table_style">
                <table class="table table-striped table-hover align-middle">
                  <thead class="table-dark">
                    <tr>
                      <th scope="col" style="width: 80px">Sr No</th>
                      <th scope="col" style="width: 150px">Key</th>
                      <th scope="col" style="min-width: 200px">Name</th>
                      <th scope="col" style="min-width: 200px">Description</th>
                      <th scope="col" style="width: 150px">Category</th>
                      <th scope="col" style="width: 120px">Default</th>
                      <th scope="col" style="width: 120px">Limits</th>
                      <th scope="col" style="width: 80px">Status</th>
                      <th scope="col" style="width: 150px">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr v-for="(feature, index) in features" :key="feature._id">
                      <td class="text-center">
                        <span class="badge bg-secondary">
                          {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </span>
                      </td>
                      <td>
                        <code class="feature-key">{{ feature.key }}</code>
                      </td>
                      <td>
                        <div>
                          <strong>{{ feature.name }}</strong>
                        </div>
                      </td>
                      <td>
                        <div class="description-cell">
                          <span :title="feature.description" class="text-muted">
                            {{ truncateText(feature.description, 50) }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span class="badge bg-info">
                          {{ getCategoryName(feature.category) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          :class="
                            feature.enabledByDefault
                              ? 'badge bg-success'
                              : 'badge bg-light text-dark'
                          "
                        >
                          {{ feature.enabledByDefault ? "Yes" : "No" }}
                        </span>
                      </td>
                      <td class="text-center">
                        <div
                          v-if="
                            feature.limits &&
                            Object.keys(feature.limits).length > 0
                          "
                        >
                          <button
                            class="btn btn-sm btn-outline-info"
                            @click="showLimits(feature.limits)"
                            title="View Limits"
                          >
                            <i class="fas fa-list"></i>
                            {{ Object.keys(feature.limits).length }}
                          </button>
                        </div>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-center">
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="feature.isActive"
                            @change="handleToggleStatus(feature._id, $event)"
                            :disabled="loading || !canUpdate('packages')"
                          />
                          <label class="form-check-label text-xs">
                            {{ feature.isActive ? "Active" : "Inactive" }}
                          </label>
                        </div>
                      </td>
                      <td>
                          <div class="d-flex gap-2" role="group">
                            <button
                              class="btn btn-sm btn-outline-primary"
                              @click="viewFeature(feature)"
                              title="View"
                              :disabled="!canView('packages')"
                            >
                              <i class="fas fa-eye"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-success"
                              @click="editFeature(feature)"
                              title="Edit"
                              :disabled="!canUpdate('packages')"
                            >
                              <i class="fas fa-edit"></i>
                            </button>
                            <button
                              class="btn btn-sm btn-outline-danger"
                              @click="deleteFeature(feature._id)"
                              title="Delete"
                              :disabled="!canDelete('packages')"
                            >
                              <i class="fas fa-trash"></i>
                            </button>
                          </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="fas fa-puzzle-piece fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No features found</h5>
                <p class="text-muted">Add your first feature to get started.</p>
                <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                  <i class="fas fa-plus me-1"></i>
                  Add Feature
                </button>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalItems > 0"
                class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top"
              >
                <div class="text-muted">
                  <small>
                    Showing {{ startItem }} to {{ endItem }} of
                    {{ totalItems }} features
                  </small>
                </div>
                <nav v-if="totalPages > 1" aria-label="Features pagination">
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage <= 1 }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage - 1)"
                        :disabled="currentPage <= 1"
                        aria-label="Previous page"
                      >
                        <i class="fas fa-chevron-left"></i>
                        <span class="ms-1 d-none d-sm-inline">Previous</span>
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"
                    >
                      <button
                        v-if="typeof page === 'number'"
                        class="page-link"
                        @click="changePage(page)"
                        :aria-label="`Go to page ${page}`"
                        :aria-current="
                          page === currentPage ? 'page' : undefined
                        "
                      >
                        {{ page }}
                      </button>
                      <span v-else class="page-link">{{ page }}</span>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage >= totalPages }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage + 1)"
                        :disabled="currentPage >= totalPages"
                        aria-label="Next page"
                      >
                        <span class="me-1 d-none d-sm-inline">Next</span>
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <FeatureModal
      v-if="showModal"
      :show="showModal"
      :feature="selectedFeature"
      :is-edit="isEditMode"
      :categories="categories"
      @close="closeModal"
      @saved="handleFeatureSaved"
    />

    <!-- View Modal -->
    <FeatureViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :feature="selectedFeature"
      :categories="categories"
      @close="closeViewModal"
      @edit="handleEditFromView"
    />

    <!-- Limits Modal -->
    <div
      v-if="showLimitsModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-list me-2"></i>
              Feature Limits
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeLimitsModal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedLimits">
              <div
                v-for="(value, key) in selectedLimits"
                :key="key"
                class="mb-2"
              >
                <strong class="text-muted"
                  >{{ formatLimitKey(String(key)) }}:</strong
                >
                <span class="ms-2">{{ formatLimitValue(value) }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeLimitsModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const FeatureModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/FeatureModal.vue")
);

const FeatureViewModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/FeatureViewModal.vue")
);

// State
const features = ref<any[]>([]);
const categories = ref<any[]>([]);
const loading = ref(false);
const searchQuery = ref("");
const filterCategory = ref("");
const filterStatus = ref("");
const sortBy = ref("name");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);

// Modal state
const showModal = ref(false);
const showViewModal = ref(false);
const showLimitsModal = ref(false);
const selectedFeature = ref<any>(null);
const selectedLimits = ref<any>(null);
const isEditMode = ref(false);

// Computed
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const startItem = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);

const endItem = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
);

const visiblePages = computed(() => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];

  if (totalPages.value <= 1) {
    return [1];
  }

  if (totalPages.value <= 7) {
    for (let i = 1; i <= totalPages.value; i++) {
      range.push(i);
    }
    return range;
  }

  for (
    let i = Math.max(2, currentPage.value - delta);
    i <= Math.min(totalPages.value - 1, currentPage.value + delta);
    i++
  ) {
    range.push(i);
  }

  if (currentPage.value - delta > 2) {
    rangeWithDots.push(1, "...");
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage.value + delta < totalPages.value - 1) {
    rangeWithDots.push("...", totalPages.value);
  } else if (totalPages.value > 1) {
    rangeWithDots.push(totalPages.value);
  }

  return rangeWithDots.filter(
    (page, index, array) => array.indexOf(page) === index
  );
});

// Methods
const fetchFeatures = async () => {
  console.log("🚀 fetchFeatures called");
  try {
    loading.value = true;
    const params: any = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (filterCategory.value) {
      params.category = filterCategory.value;
    }

    if (filterStatus.value !== "") {
      params.isActive = filterStatus.value;
    }

    if (sortBy.value) {
      params.sortBy = sortBy.value;
    }

    const response = await apiService.getFeatures(params);

    console.log("🔍 Features API Response:", response);
    console.log("🔍 Response structure:", {
      hasData: !!response.data,
      hasFeatures: !!(response.data && response.data.features),
      directFeatures: !!response.features,
      responseKeys: Object.keys(response || {}),
      dataKeys: response.data ? Object.keys(response.data) : null,
    });

    // Handle the nested response structure
    if (response.data && response.data.features) {
      console.log("✅ Using response.data.features path");
      features.value = response.data.features;
      totalItems.value =
        response.data.pagination?.totalItems || response.data.features.length;
    } else if (response.features) {
      console.log("✅ Using response.features path");
      features.value = response.features;
      totalItems.value =
        response.pagination?.totalItems || response.features.length;
    } else {
      console.log("✅ Using fallback path");
      features.value = response.data || response || [];
      totalItems.value =
        response.total || response.pagination?.total || features.value.length;
    }

    console.log("🔍 Final features array:", features.value);
    console.log("🔍 Final totalItems:", totalItems.value);
  } catch (error: any) {
    console.error("❌ Error fetching features:", error);
    console.error("❌ Error details:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    toast.error("Failed to load features");
  } finally {
    loading.value = false;
  }
};

const fetchCategories = async () => {
  try {
    // Assuming we have a categories endpoint - you might need to adjust this
    const response = await apiService.getPackageCategories({ limit: 100 });
    if (response.data && response.data.categories) {
      categories.value = response.data.categories;
    } else if (response.categories) {
      categories.value = response.categories;
    } else {
      categories.value = response.data || response || [];
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
    // Continue without categories if they fail to load
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchFeatures();
};

const handleFilter = () => {
  currentPage.value = 1;
  fetchFeatures();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterCategory.value = "";
  filterStatus.value = "";
  sortBy.value = "name";
  currentPage.value = 1;
  fetchFeatures();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchFeatures();
  }
};

const openCreateModal = () => {
  selectedFeature.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const editFeature = (feature: any) => {
  selectedFeature.value = { ...feature };
  isEditMode.value = true;
  showModal.value = true;
};

const viewFeature = (feature: any) => {
  selectedFeature.value = feature;
  showViewModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedFeature.value = null;
  isEditMode.value = false;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedFeature.value = null;
};

const handleEditFromView = (feature: any) => {
  closeViewModal();
  editFeature(feature);
};

const handleFeatureSaved = () => {
  fetchFeatures();
  closeModal();
  toast.success(
    isEditMode.value
      ? "Feature updated successfully"
      : "Feature created successfully"
  );
};

const handleToggleStatus = (id: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  toggleStatus(id, target.checked);
};

const toggleStatus = async (id: string, status: boolean) => {
  try {
    // Optimistic update
    const featureIndex = features.value.findIndex((feat) => feat._id === id);
    if (featureIndex !== -1) {
      features.value[featureIndex].isActive = status;
    }

    await apiService.toggleFeatureStatus(id);
    toast.success(
      `Feature ${status ? "activated" : "deactivated"} successfully`
    );

    await fetchFeatures();
  } catch (error) {
    console.error("Error toggling status:", error);
    toast.error("Failed to update feature status");

    // Revert optimistic update
    const featureIndex = features.value.findIndex((feat) => feat._id === id);
    if (featureIndex !== -1) {
      features.value[featureIndex].isActive = !status;
    }
  }
};

const deleteFeature = async (id: string) => {
  if (
    !confirm(
      "Are you sure you want to delete this feature? This action cannot be undone."
    )
  ) {
    return;
  }

  try {
    await apiService.deleteFeature(id);
    await fetchFeatures();
    toast.success("Feature deleted successfully");
  } catch (error) {
    console.error("Error deleting feature:", error);
    toast.error("Failed to delete feature");
  }
};

const showLimits = (limits: any) => {
  selectedLimits.value = limits;
  showLimitsModal.value = true;
};

const closeLimitsModal = () => {
  showLimitsModal.value = false;
  selectedLimits.value = null;
};

// Utility functions
const getCategoryName = (category: any): string => {
  // Handle both populated category object and category ID string
  if (typeof category === "object" && category !== null) {
    return category.name || "Unknown";
  } else if (typeof category === "string") {
    const cat = categories.value.find((cat) => cat._id === category);
    return cat ? cat.name : "Unknown";
  }
  return "Unknown";
};

const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "-";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const formatLimitKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/([a-z])([A-Z])/g, "$1 $2");
};

const formatLimitValue = (value: any): string => {
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  return value?.toString() || "-";
};

// Lifecycle
onMounted(() => {
  fetchCategories();
  fetchFeatures();
});
</script>

<style scoped>
.feature-key {
  background-color: #f8f9fa;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: "Courier New", monospace;
}

.description-cell {
  max-width: 200px;
}

/* Enhanced table header styling */
.table thead.table-dark th {
  background-color: var(--bs-dark) !important;
  color: var(--bs-white) !important;
  border-color: var(--bs-dark) !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}

.table th,
.table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table {
  margin-bottom: 0;
}

/* Enhanced pagination styling */
.pagination .page-link {
  color: #495057;
  background-color: #fff;
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  border-radius: 0;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;
}

.pagination .page-link:hover {
  z-index: 2;
  color: #0056b3;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item:first-child .page-link {
  margin-left: 0;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.pagination .page-item:last-child .page-link {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.pagination .page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Table responsiveness improvements */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.btn-group .btn {
  border-radius: 0.25rem;
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.badge {
  font-size: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
}

/* Card styling improvements */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.table_style table{
  min-width: 1500px;
}
.table_style{
  overflow-x:auto;
}
</style>
